import { BookingFolioInfo } from '@new/domains/bookings/_types'
import { BookingFeeInput } from '@new/domains/hotelTapeChart/HotelTapeChart.types'

import {
  BOOKING_ALTERATION_MODE_CUSTOM_FIELD_ID,
  BOOKING_ALTERATION_MODE_ROOM_TYPE,
  getCustomFieldValue,
} from '@/utils/customFields'

import { FeeOverride } from './components/ModifyBookingForm/ModifyBookingForm.types'
import { DatePrices, OriginalNights } from './ModifyBookingSidePanel.definitions'
import { ModifyPreview } from './ModifyBookingSidePanel.types'

interface AlterationModeBooking {
  hotelRoomType?: { id: string } | null
  customFields?: { fields: { id: string; value?: string | null }[] }[]
}

export const getIsRoomTypeAlteration = (booking?: AlterationModeBooking): boolean =>
  !!booking?.hotelRoomType &&
  getCustomFieldValue(booking?.customFields ?? [], BOOKING_ALTERATION_MODE_CUSTOM_FIELD_ID) ===
    BOOKING_ALTERATION_MODE_ROOM_TYPE

export type BookingModificationRoute = 'hotel' | 'property'

interface GetBookingModificationRouteParams {
  isRoomTypeView: boolean
}

export const getBookingModificationRoute = ({
  isRoomTypeView,
}: GetBookingModificationRouteParams): BookingModificationRoute =>
  isRoomTypeView ? 'hotel' : 'property'

interface GetExpectedTotalParams {
  isLockPrice?: boolean
  originalValueNightsAndFees?: number
  newValueNightsAndFees?: number
}

// The hotel alteration reconciles against this, so it has to match what the panel promises: the
// original value while the price is locked, and nights/fees/taxes otherwise — never `total`,
// which also carries servicesSubtotal.
export const getExpectedTotal = ({
  isLockPrice,
  originalValueNightsAndFees,
  newValueNightsAndFees,
}: GetExpectedTotalParams): number | undefined =>
  isLockPrice ? originalValueNightsAndFees : newValueNightsAndFees

interface HasSavableChangeParams {
  isUnitOnlyRestricted: boolean
  isRoomTypeView: boolean
  hasChangedRoomTypeOrUnit: boolean
  hasChangedProperty: boolean
  hasModifiedOriginalPeriod: boolean
  hasChangedHotelPricing: boolean
  hasOverwrittenNightsOrFees: boolean
  hasChangedSummary: boolean
}

export const hasSavableChange = ({
  isUnitOnlyRestricted,
  isRoomTypeView,
  hasChangedRoomTypeOrUnit,
  hasChangedProperty,
  hasModifiedOriginalPeriod,
  hasChangedHotelPricing,
  hasOverwrittenNightsOrFees,
  hasChangedSummary,
}: HasSavableChangeParams): boolean => {
  // A unit-only restriction outranks the view, matching getBookingModificationRoute.
  if (isUnitOnlyRestricted) return isRoomTypeView ? hasChangedRoomTypeOrUnit : hasChangedProperty
  if (isRoomTypeView)
    return hasChangedRoomTypeOrUnit || hasModifiedOriginalPeriod || hasChangedHotelPricing
  return (
    hasChangedProperty ||
    hasModifiedOriginalPeriod ||
    hasOverwrittenNightsOrFees ||
    hasChangedSummary
  )
}

// The hotel room-type preview reprices against current rates, so the booked total can differ from
// the preview total before any edit. Any difference counts as a change worth saving.
export const hasHotelPricingChanged = (originalTotal?: number, newTotal?: number): boolean =>
  originalTotal != null && newTotal != null && originalTotal !== newTotal

// The hotel alteration mutation takes fees as BookingFeeInput; map the form's fee overrides to it.
export const mapFeeOverridesToBookingFees = (
  feeOverrides?: FeeOverride[],
): BookingFeeInput[] | undefined =>
  feeOverrides?.map(fee => ({
    id: fee.feeId,
    itemId: fee.itemId,
    name: fee.name,
    amount: fee.value,
  }))

interface HasHotelRoomTypeOrUnitChangedParams {
  selectedHotelRoomTypeId?: string
  selectedUnitId?: string
  bookingRoomTypeId?: string
  bookingHomeId?: string
  bookingIsRoomAssigned?: boolean
}

// A field only counts as changed once it holds a value, since an unseeded field reads as undefined.
// The unit select clears to an empty string, which is what tells a deliberate unassign apart.
export const hasHotelRoomTypeOrUnitChanged = ({
  selectedHotelRoomTypeId,
  selectedUnitId,
  bookingRoomTypeId,
  bookingHomeId,
  bookingIsRoomAssigned = true,
}: HasHotelRoomTypeOrUnitChangedParams): boolean => {
  const hasUnassignedUnit = selectedUnitId === '' && bookingIsRoomAssigned && !!bookingHomeId

  return (
    (!!selectedHotelRoomTypeId && selectedHotelRoomTypeId !== bookingRoomTypeId) ||
    (!!selectedUnitId && (!bookingIsRoomAssigned || selectedUnitId !== bookingHomeId)) ||
    hasUnassignedUnit
  )
}

export interface BookingValuesReview {
  isLockPrice?: boolean
  taxAndFeesTotal: number
  summary: {
    value: number
    total: number
    balance: number
    valueNightsAndFees: number
  }
  nights: {
    pricesTotal: number
    balance: number
    balanceCount: number
    count: number
  }
}

// Return removed nights and total price of these nights
export const getRemovedNights = (
  newValues?: ModifyPreview,
  bookingFolio?: BookingFolioInfo,
): {
  removedNights?: OriginalNights[]
  removedNightsBalance?: number
} => {
  const originalNights = bookingFolio?.items
    .filter(folio => folio.kind === 'NIGHT')
    .map(folio => ({
      date: folio.date,
      price: folio.total,
    }))

  const newNights = newValues?.items
    ?.filter(folio => folio.kind === 'NIGHT')
    .map(folio => ({
      date: folio.date,
      price: folio.total,
    }))

  const newNightDates = newNights?.map(night => night.date)

  // Compares all nights and remove original dates to only get added nights
  const removedNights = originalNights?.filter(night => !newNightDates?.includes(night.date))
  const removedNightsBalance = removedNights?.reduce(
    (accumulator, night) => night.price + accumulator,
    0,
  )

  return { removedNights, removedNightsBalance }
}

// Return added nights and total price of these nights
export const getAddedNights = (
  newValues: ModifyPreview,
  bookingFolio: BookingFolioInfo,
): {
  addedNights: OriginalNights[]
  addedNightsBalance: number
} => {
  const originalNights = bookingFolio.items
    .filter(folio => folio.kind === 'NIGHT')
    .map(folio => ({
      date: folio.date,
      price: folio.total,
    }))

  const newNights = newValues.items
    .filter(folio => folio.kind === 'NIGHT')
    .map(folio => ({
      date: folio.date,
      price: folio.total,
    }))

  const originalNightDates = originalNights.map(night => night.date)

  // Compares all nights and remove original dates to only get added nights
  const addedNights = newNights.filter(
    night => night?.date && !originalNightDates.includes(night.date),
  )
  const addedNightsBalance = addedNights?.reduce(
    (accumulator, night) => night.price + accumulator,
    0,
  )

  return { addedNights, addedNightsBalance }
}

export const getNewBookingValues = (
  newValues?: ModifyPreview,
  bookingFolio?: BookingFolioInfo,
): BookingValuesReview | undefined => {
  if (!newValues || !bookingFolio) return undefined

  const taxAndFeesTotal = newValues.items.reduce((accumulator, item) => {
    if (item.kind === 'TAX' || item.kind === 'FEE') return accumulator + item.total
    return accumulator
  }, 0)

  const newNights = newValues.items.filter(item => item.kind === 'NIGHT')

  const nightlyPricesTotal = newNights.reduce((accumulator, item) => accumulator + item.total, 0)

  const { addedNights, addedNightsBalance } = getAddedNights(newValues, bookingFolio)
  const { removedNights, removedNightsBalance } = getRemovedNights(newValues, bookingFolio)

  const nights = {
    pricesTotal: nightlyPricesTotal,
    balance: addedNightsBalance - (removedNightsBalance || 0),
    balanceCount: addedNights.length - (removedNights || []).length,
    count: newNights.length,
  }

  const summary = newValues?.newSummary

  return { taxAndFeesTotal, summary, nights }
}

export const getModifyRestrictionMessage = (reason?: string) =>
  `You will be able to only simulate the rates and send them to the guest. ${reason}.`

// Narrows status-blocked dates to the ones the room type has no free unit for
// (availabilityCount === 0). A date with a free unit stays open even if its aggregate status
// looks occupied.
export const filterDatesWithoutAvailability = (
  dates: string[],
  availability: DatePrices[],
): string[] => {
  const unavailableDates = new Set(
    availability
      .filter(datePrice => datePrice.availabilityCount === 0)
      .map(datePrice => datePrice.date),
  )
  return dates.filter(date => unavailableDates.has(date))
}
